import {
    Checkbox,
    Text,
    TextTag,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import useComponentVisible from '../../../hooks/useComponentVisible'
import {OptionsGroup} from '../../../utils/filters/Option'
import {TextAppearance} from '@volkswagen-onehub/tokens'
import {useTranslation} from 'react-i18next'
import MultiSelectStatus from '../ui/MultiSelectStatus'
import {Params} from '../../../shared/variables'
import {readHashParam, setHashParam} from '../../../utils/url/hashParams'

enum OptionsIndexes {
    MAIN_CATEGORY,
    NESTED_OPTIONS,
}

enum VwMultiSelectClassNames {
    OPTIONS_OPEN = 'show',
}

export interface VwMultiSelectProps {
    placeholderName: string
    options: OptionsGroup
    selectedOptionsFromState: string[]
    onChangeMain: (value: string[]) => void
    onChangeChildren: (value: string) => void
}

const VwMultiSelectContainer = styled.a`
    --hover-circle-size: 48px;

    position: relative;
    padding-top: 7px;
    padding-bottom: 7px;
    border-radius: ${(props) => props.theme.size.borderRadius.select};
    box-shadow: 0 0 0 ${(props) => props.theme.size.border.default}
        ${(props) => props.theme.colors.border.default};

    &:hover {
        box-shadow: 0 0 0 ${(props) => props.theme.size.border.default}
            ${(props) => props.theme.colors.border.hover};
        transition: box-shadow 300ms;

        > span {
            color: ${(props) => props.theme.colors.button.primary.hover};

            > span {
                color: ${(props) => props.theme.colors.button.primary.hover};
            }
        }
    }

    &:focus,
    &:focus-within {
        box-shadow: 0 0 0 ${(props) => props.theme.size.border.focus}
            ${(props) => props.theme.colors.focus.main};
    }
`

const VwMultiSelectPlaceholder = styled(Text)`
    align-items: center;
    cursor: pointer;
    display: flex;
    min-height: 34px;
    padding-bottom: 4px;
    padding-top: 4px;
    padding-right: 16px;
    padding-left: 16px;
    width: 100%;

    &:hover {
        svg {
            color: ${(props) => props.theme.colors.content.primary};
        }
    }

    + .${VwMultiSelectClassNames.OPTIONS_OPEN} {
        margin-top: 20px;
    }
`

const VwMultiSelectIconContainer = styled(Text)`
    align-items: center;
    display: flex;
    width: var(--hover-circle-size);
    height: var(--hover-circle-size);
    justify-content: center;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);

    svg {
        position: absolute;
        transition: transform 0.3s cubic-bezier(0.14, 1.12, 0.67, 0.99) 0s;
    }
`

const VwMultiSelectOptionsCounter = styled(Text)`
    --vw-multi-select-options-counter-size: 16px;

    width: var(--vw-multi-select-options-counter-size);
    height: var(--vw-multi-select-options-counter-size);
    padding: 1px;
    background-color: ${(props) => props.theme.colors.background.alert};
    border-radius: ${(props) => props.theme.size.borderRadius.badge};
    color: ${(props) => props.theme.colors.content.onsurface};
    text-align: center;
    line-height: 1em;
    position: absolute;
    top: 50%;
    right: 45px;
    transform: translateY(-50%);
`

const VwMultiSelectOptions = styled.ul`
    --vw-multi-select-list-item-margin-bottom: 17px;

    background: ${(props) => props.theme.colors.background.primary};
    box-shadow: ${(props) => props.theme.colors.shadow.default};
    border-radius: ${(props) => props.theme.size.borderRadius.select};
    list-style-type: none;
    opacity: 0;
    margin: 0;
    padding: 16px;
    position: absolute;
    transform: translateX(-100vw);
    width: 100%;
    z-index: 1;

    &.${VwMultiSelectClassNames.OPTIONS_OPEN} {
        opacity: 1;
        transform: translateX(0);
    }

    li {
        margin: 0 0 var(--vw-multi-select-list-item-margin-bottom);

        &:last-child {
            margin: 0;
        }
    }
`

const VwMultiSelectNestedOptionsList = styled.ul`
    --vw-multi-select-list-item-margin-bottom: 10px;

    list-style: none;
    margin-top: 10px;
    padding-left: 34px;
`

export default function VwMultiSelect(
    props: VwMultiSelectProps
): React.JSX.Element {
    const {t} = useTranslation('app')
    const optionsArray = Object.entries(props.options)
    const {ref, isComponentVisible, setIsComponentVisible} =
        useComponentVisible(false)
    const [multiselectStatusMessage, setMultiselectStatusMessage] =
        useState<string>(
            t('filters.noneSelected', {
                title: t('filters.aria-labels.carName'),
            })
        )

    const allNestedValues = useMemo(
        () =>
            optionsArray.reduce<string[]>(
                (acc, opt) =>
                    acc.concat(opt[OptionsIndexes.NESTED_OPTIONS] as string[]),
                []
            ),
        [optionsArray]
    )

    function handleMainCheckboxChange(values: string[]): void {
        const newValuesNotAlreadySelected = values.filter(
            (value) => !props.selectedOptionsFromState.includes(value)
        )

        if (newValuesNotAlreadySelected.length === 0) {
            props.onChangeMain(values)

            return
        }

        props.onChangeMain(newValuesNotAlreadySelected)
    }

    function handleChildCheckboxChange(value: string): void {
        props.onChangeChildren(value)
    }

    function getNestedOptions(option: [string, string[]]): string[] {
        return option[OptionsIndexes.NESTED_OPTIONS].map((nested) => nested)
    }

    const appliedFromUrlRef = useRef(false)
    const prevSelectedRef = useRef<string[]>([])
    useEffect(() => {
        if (appliedFromUrlRef.current) {
            return
        }
        if (!allNestedValues.length) {
            return
        }

        const raw = readHashParam(`${Params.NAMES}-app`)
        if (raw) {
            const urlValues = raw
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            const validValues = urlValues.filter((v) =>
                allNestedValues.includes(v)
            )
            if (validValues.length) {
                props.onChangeMain(validValues)
                setMultiselectStatusMessage(
                    t('filters.statusMessage', {
                        title: t('filters.aria-labels.carName'),
                        values: validValues.join(', '),
                    })
                )
            }
        }

        appliedFromUrlRef.current = true
        prevSelectedRef.current = props.selectedOptionsFromState
    }, [allNestedValues, t, props])

    useEffect(() => {
        if (!appliedFromUrlRef.current) {
            prevSelectedRef.current = props.selectedOptionsFromState

            return
        }

        const prev = prevSelectedRef.current.join(',')
        const curr = props.selectedOptionsFromState.join(',')
        if (prev === curr) {
            return
        }

        setHashParam(Params.NAMES, curr)
        prevSelectedRef.current = props.selectedOptionsFromState
    }, [props.selectedOptionsFromState])

    useEffect(() => {
        if (props.selectedOptionsFromState.length > 0) {
            setMultiselectStatusMessage(
                t('filters.statusMessage', {
                    title: t('filters.aria-labels.carName'),
                    values: props.selectedOptionsFromState.join(', '),
                })
            )
        } else {
            setMultiselectStatusMessage(
                t('filters.noneSelected', {
                    title: t('filters.aria-labels.carName'),
                })
            )
        }
    }, [props.selectedOptionsFromState, t])

    useEffect(() => {
        if (isComponentVisible) {
            const firstCheckbox = document.querySelector(
                '#vw-multi-select-options input'
            ) as HTMLElement | null
            firstCheckbox?.focus()
        } else {
            const multiselectContainer = document.querySelector(
                '#vw-multi-select-category'
            ) as HTMLElement | null
            multiselectContainer?.focus()
        }
    }, [isComponentVisible])

    return (
        <>
            <VwMultiSelectContainer
                ref={ref}
                onClick={() => {
                    setIsComponentVisible(!isComponentVisible)
                }}
                role="button"
                id="vw-multi-select-category"
                tabIndex={0}
                aria-expanded={isComponentVisible}
                aria-controls="vw-multi-select-options"
                onKeyDown={(e) => {
                    switch (e.key) {
                        case 'Enter':
                        case ' ':
                            setIsComponentVisible(true)
                            break
                        case 'Escape':
                            setIsComponentVisible(false)
                            ref.current?.focus()
                            break
                        default:
                    }
                }}
            >
                <VwMultiSelectPlaceholder
                    tag={TextTag.span}
                    color={TokenTextColor.primary}
                >
                    {props.placeholderName}

                    {props.selectedOptionsFromState.length ? (
                        <VwMultiSelectOptionsCounter
                            tag={TextTag.span}
                            bold
                            appearance={TextAppearance.copy100}
                        >
                            {props.selectedOptionsFromState.length}
                        </VwMultiSelectOptionsCounter>
                    ) : undefined}

                    <VwMultiSelectIconContainer
                        tag={TextTag.span}
                        color={TokenTextColor.primary}
                    >
                        <svg
                            fill="currentColor"
                            height="24"
                            role="img"
                            viewBox="0 0 24 24"
                            width="24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M1.984 6.19l9.465 9.893c.28.299.418.397.551.397.145 0 .31-.114.572-.392l9.443-9.898.962.918-9.439 9.893a2.108 2.108 0 01-1.538.81 2.083 2.083 0 01-1.517-.813l-9.46-9.889z" />
                        </svg>
                    </VwMultiSelectIconContainer>
                </VwMultiSelectPlaceholder>

                <VwMultiSelectOptions
                    className={`${
                        isComponentVisible
                            ? VwMultiSelectClassNames.OPTIONS_OPEN
                            : ''
                    }`}
                    id="vw-multi-select-options"
                    aria-describedby="vw-multi-select-category"
                    role="listbox"
                    aria-multiselectable="true"
                >
                    {optionsArray.map((option, index) => (
                        <li key={index}>
                            <Checkbox
                                label={
                                    <Text bold>
                                        {option[OptionsIndexes.MAIN_CATEGORY]}
                                    </Text>
                                }
                                value={option[OptionsIndexes.MAIN_CATEGORY]}
                                onChange={() =>
                                    handleMainCheckboxChange(
                                        getNestedOptions(option)
                                    )
                                }
                                checked={getNestedOptions(option).every(
                                    (value) =>
                                        props.selectedOptionsFromState.includes(
                                            value
                                        )
                                )}
                                tabIndex={isComponentVisible ? 0 : -1}
                            />

                            {option[OptionsIndexes.NESTED_OPTIONS].length ? (
                                <VwMultiSelectNestedOptionsList aria-describedby="vw-multi-select-category">
                                    {option[OptionsIndexes.NESTED_OPTIONS].map(
                                        (nestedOption, nestedIndex) => (
                                            <li key={nestedIndex}>
                                                <Checkbox
                                                    label={nestedOption}
                                                    value={nestedOption}
                                                    checked={props.selectedOptionsFromState.includes(
                                                        nestedOption
                                                    )}
                                                    onChange={() =>
                                                        handleChildCheckboxChange(
                                                            nestedOption
                                                        )
                                                    }
                                                    tabIndex={
                                                        isComponentVisible
                                                            ? 0
                                                            : -1
                                                    }
                                                />
                                            </li>
                                        )
                                    )}
                                </VwMultiSelectNestedOptionsList>
                            ) : undefined}
                        </li>
                    ))}
                </VwMultiSelectOptions>
            </VwMultiSelectContainer>
            {multiselectStatusMessage && (
                <MultiSelectStatus message={multiselectStatusMessage} />
            )}
        </>
    )
}
